import React, { useState } from "react";
import PackageImageUploadModal from "../../../Pages/Shipper/Customer/MainPages/Shipments/Components/PackageImageUploadModal/PackageImageUploadModal";
import BoltButton from "../../UI/Buttons/BoltButton";
import { MdCheckCircle, MdCloudUpload, MdInfo } from "react-icons/md";
import { confirmOneTimeShipperShipment } from "../../../services/admin.services";
import StatusIcons from "../../StatusIcons/StatusIcons";
import { useTranslation } from "react-i18next";

const OneTimeShipperAdminApproval = ({
  shipmentData,
  refreshShipmentData,
  setSnack,
  getAllShipments,
}) => {
  const { t } = useTranslation("finances");
  const [packageImageUploadModal, setPackageImageUploadModal] = useState(false);
  const [confirming, setConfirming] = useState(false);

  if (!shipmentData) return null;

  if (shipmentData?.shipment_type !== "oneTimeShipper") {
    return null;
  }

  const hasImages =
    shipmentData?.one_time_package_images &&
    Array.isArray(shipmentData.one_time_package_images) &&
    shipmentData.one_time_package_images.length > 0;

  const carrierName =
    shipmentData?.shipping_company?.shipping_company?.name ||
    shipmentData?.shipping_company?.name ||
    shipmentData?.shipping_company?.shipping_company?.shipping_company?.name ||
    "";

  const formatDate = (value) => {
    if (!value) return "";
    return value.length >= 10 ? value.slice(0, 10) : value;
  };

  const formatTime = (value) => {
    if (!value) return "";
    return value.length >= 5 ? value.slice(0, 5) : value;
  };

  const pickupDateDisplay = formatDate(
    shipmentData?.PickupDate ||
      shipmentData?.shipdate ||
      shipmentData?.ship_date ||
      shipmentData?.readydate
  );

  const pickupOpenTime = formatTime(shipmentData?.readyopentime);
  const pickupCloseTime = formatTime(shipmentData?.readyclosetime);
  const pickupTimeRange = [pickupOpenTime, pickupCloseTime]
    .filter(Boolean)
    .join(" - ");

  const handleConfirmShipment = async () => {
    if (!shipmentData?.id) return;
    setConfirming(true);
    try {
      const response = await confirmOneTimeShipperShipment(shipmentData.id);

      if (!response?.error) {
        if (setSnack) {
          setSnack((snack) => ({
            ...snack,
            open: true,
            text: t("invoice.oneTime.confirmed"),
            severity: "success",
          }));
        }

        if (refreshShipmentData && shipmentData?.id) {
          setTimeout(() => {
            refreshShipmentData(shipmentData.id);
          }, 500);
        }

        if (getAllShipments) {
          getAllShipments(1, 20, "");
        }
      } else {
        if (setSnack) {
          setSnack((snack) => ({
            ...snack,
            open: true,
            text: t("invoice.oneTime.confirmFailed"),
            severity: "error",
          }));
        }
      }
    } catch (error) {
      if (setSnack) {
        setSnack((snack) => ({
          ...snack,
          open: true,
          text: t("common:messages.somethingWentWrong"),
          severity: "error",
        }));
      }
    } finally {
      setConfirming(false);
    }
  };

  const metaItems = [
    { label: t("invoice.oneTime.carrier"), value: carrierName },
    { label: t("invoice.oneTime.pickupDate"), value: pickupDateDisplay },
    { label: t("invoice.oneTime.pickupTime"), value: pickupTimeRange },
  ].filter((item) => item.value);

  return (
    <div className="my-2 mx-4">
      <div className="rounded-lg border border-ui-border bg-ui-surface shadow-sm">
        <div className="flex items-center justify-between px-3 py-2 border-b border-ui-border">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-full bg-brand-light flex items-center justify-center">
              <MdInfo className="text-brand" size={16} />
            </div>
            <div>
              <div className="text-sm font-semibold text-ui-text leading-none">
                {t("invoice.oneTime.approvalTitle")}
              </div>
              <div className="text-[11px] text-ui-text-muted">
                {t("invoice.oneTime.review")}
              </div>
            </div>
          </div>
          <div className="flex-shrink-0">
            <StatusIcons
              status={shipmentData?.shipment_status || "Pending"}
              labelClassName="text-[10px]"
            />
          </div>
        </div>

        <div className="px-3 py-2 space-y-2">
          {metaItems.length > 0 && (
            <div className="flex flex-wrap gap-2 text-[11px]">
              {metaItems.map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-1 px-2 py-1 rounded-full bg-ui-surface-sunken border border-ui-border"
                >
                  <span className="uppercase text-ui-text-muted tracking-wide">
                    {t("invoice.labelColon", { label: item.label })}
                  </span>
                  <span className="text-ui-text font-semibold">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          )}

          {hasImages && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="leading-tight">
                  <div className="text-sm font-semibold text-ui-text">
                    {t("invoice.oneTime.packageImages")}
                  </div>
                  <div className="text-[11px] text-ui-text-muted">
                    {t("invoice.oneTime.uploadedCount", {
                      count: shipmentData.one_time_package_images.length,
                    })}
                    {shipmentData.one_time_package_images.length === 0 &&
                      t("invoice.oneTime.yet")}
                  </div>
                </div>
                <BoltButton
                  label={t("invoice.oneTime.uploadMore")}
                  onClick={() => setPackageImageUploadModal(true)}
                  variant="primary"
                  size="xs"
                  icon={MdCloudUpload}
                />
              </div>
              <div className="grid grid-cols-[repeat(auto-fill,minmax(110px,1fr))] gap-1.5">
                {shipmentData.one_time_package_images.map(
                  (imageItem, index) => (
                    <div
                      key={imageItem.id || index}
                      className="relative w-full pt-[68%] rounded-lg overflow-hidden border border-ui-border bg-ui-surface-sunken"
                    >
                      <img
                        src={imageItem.image}
                        alt={
                          imageItem.image_name ||
                          t("invoice.oneTime.imageAlt", { n: index + 1 })
                        }
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 ring-1 ring-inset ring-black/5" />
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {!hasImages && (
            <div className="rounded-lg border border-dashed border-ui-border px-3 py-2 bg-ui-surface-sunken text-center">
              <div className="text-sm font-medium text-ui-text">
                {t("invoice.oneTime.notUploaded")}
              </div>
              <div className="text-xs text-ui-text-muted mb-2">
                {t("invoice.oneTime.askCustomer")}
              </div>
              <BoltButton
                label={t("invoice.oneTime.uploadImages")}
                onClick={() => setPackageImageUploadModal(true)}
                variant="primary"
                size="xs"
                icon={MdCloudUpload}
              />
            </div>
          )}

          <div className="flex flex-wrap gap-1.5 justify-end border-t border-ui-border pt-2">
            <BoltButton
              label={
                confirming
                  ? t("invoice.oneTime.confirming")
                  : t("invoice.oneTime.confirmShipment")
              }
              onClick={handleConfirmShipment}
              variant="primary"
              size="sm"
              disabled={confirming}
              loading={confirming}
              icon={MdCheckCircle}
            />
          </div>
        </div>
      </div>

      <PackageImageUploadModal
        open={packageImageUploadModal}
        onClose={() => {
          setPackageImageUploadModal(false);
        }}
        shipment={shipmentData}
        onSuccess={() => {
          setPackageImageUploadModal(false);
          if (refreshShipmentData && shipmentData?.id) {
            setTimeout(() => {
              refreshShipmentData(shipmentData.id);
            }, 500);
          }
          if (getAllShipments) {
            getAllShipments(1, 20, "");
          }
        }}
      />
    </div>
  );
};

export default OneTimeShipperAdminApproval;
